/**
 * SmartAnalysisTuningDialog
 * Entity-based tuning interface for import analysis
 * Uses deterministic rules, NOT LLM re-runs
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Settings2,
  Sparkles,
  RefreshCw,
  Check,
  ArrowRight,
  TrendingUp,
  Building2,
  Calendar,
  Hash,
} from "lucide-react";
import { trpc } from "@/lib/trpc";

// Types matching the backend
interface TuningToggle {
  id: string;
  category: "vendor" | "format" | "categorization" | "normalization";
  name: string;
  description: string;
  enabled: boolean;
  impact: "high" | "medium" | "low";
  examples?: string[];
}

interface TuningStats {
  totalProcessed: number;
  vendorsExtracted: number;
  aliasesMerged: number;
  prefixesStripped: number;
}

interface ProcessedVendor {
  original: string;
  normalized: string;
  confidence: number;
  appliedRules: string[];
}

interface SmartAnalysisTuningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number;
  sessionId?: number;
  descriptions: string[];
  onApply?: (
    toggles: TuningToggle[],
    results: { vendors: ProcessedVendor[]; stats: TuningStats }
  ) => void;
}

function getCategoryIcon(category: TuningToggle["category"]) {
  switch (category) {
    case "vendor":
      return <Building2 className="h-4 w-4" />;
    case "format":
      return <Hash className="h-4 w-4" />;
    case "categorization":
      return <TrendingUp className="h-4 w-4" />;
    case "normalization":
      return <Calendar className="h-4 w-4" />;
  }
}

function getImpactColor(impact: TuningToggle["impact"]) {
  switch (impact) {
    case "high":
      return "destructive";
    case "medium":
      return "default";
    case "low":
      return "secondary";
  }
}

export default function SmartAnalysisTuningDialog({
  open,
  onOpenChange,
  companyId,
  sessionId,
  descriptions,
  onApply,
}: SmartAnalysisTuningDialogProps) {
  const [toggles, setToggles] = useState<TuningToggle[]>([]);
  const [previewResults, setPreviewResults] = useState<{
    vendors: ProcessedVendor[];
    stats: TuningStats;
  } | null>(null);
  const [cycleCount, setCycleCount] = useState(0);

  // Fetch available toggles
  const { data: defaultToggles, isLoading: loadingToggles } =
    trpc.enhancedImport.getTuningToggles.useQuery(
      { companyId, sessionId },
      { enabled: open }
    );

  // Apply tuning mutation
  const applyTuning = trpc.enhancedImport.applyTuningToggles.useMutation();

  // Initialize toggles from defaults
  useEffect(() => {
    if (defaultToggles && toggles.length === 0) {
      setToggles(defaultToggles);
    }
  }, [defaultToggles, toggles.length]);

  // Run preview when toggles change
  const runPreview = async () => {
    if (!sessionId || descriptions.length === 0) return;

    const result = await applyTuning.mutateAsync({
      companyId,
      sessionId,
      toggles: toggles.map(t => ({ id: t.id, enabled: t.enabled })),
      descriptions,
    });

    setPreviewResults({
      vendors: result.processedVendors,
      stats: result.stats,
    });
    setCycleCount(result.cycleNumber);
  };

  // Toggle a setting
  const handleToggle = (toggleId: string) => {
    setToggles(prev =>
      prev.map(t => (t.id === toggleId ? { ...t, enabled: !t.enabled } : t))
    );
  };

  // Apply and close
  const handleApply = () => {
    if (previewResults && onApply) {
      onApply(toggles, previewResults);
    }
    onOpenChange(false);
  };

  // Group toggles by category
  const groupedToggles = toggles.reduce(
    (acc, toggle) => {
      if (!acc[toggle.category]) {
        acc[toggle.category] = [];
      }
      acc[toggle.category].push(toggle);
      return acc;
    },
    {} as Record<string, TuningToggle[]>
  );

  const categoryLabels: Record<string, string> = {
    vendor: "Vendor Extraction",
    format: "Format Detection",
    categorization: "Categorization",
    normalization: "Data Normalization",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Smart Analysis Tuning
            {cycleCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                Cycle #{cycleCount}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Adjust extraction rules without re-running AI analysis. Changes are
            deterministic and instant.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex gap-4">
          {/* Left: Toggle Controls */}
          <div className="w-1/2 overflow-hidden flex flex-col">
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-6">
                {Object.entries(groupedToggles).map(
                  ([category, categoryToggles]) => (
                    <div key={category}>
                      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
                        {getCategoryIcon(category as TuningToggle["category"])}
                        {categoryLabels[category] || category}
                      </h4>
                      <div className="space-y-3">
                        {categoryToggles.map(toggle => (
                          <Card key={toggle.id} className="p-3">
                            <div className="flex items-start gap-3">
                              <Switch
                                checked={toggle.enabled}
                                onCheckedChange={() => handleToggle(toggle.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">
                                    {toggle.name}
                                  </span>
                                  <Badge
                                    variant={getImpactColor(toggle.impact)}
                                    className="text-xs"
                                  >
                                    {toggle.impact} impact
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {toggle.description}
                                </p>
                                {toggle.examples &&
                                  toggle.examples.length > 0 && (
                                    <div className="mt-2 text-xs font-mono bg-muted/50 p-2 rounded">
                                      {toggle.examples
                                        .slice(0, 2)
                                        .map((ex, i) => (
                                          <div key={i} className="truncate">
                                            {ex}
                                          </div>
                                        ))}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </ScrollArea>

            <div className="pt-4">
              <Button
                onClick={runPreview}
                disabled={applyTuning.isPending || descriptions.length === 0}
                className="w-full"
              >
                {applyTuning.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Preview Changes
                  </>
                )}
              </Button>
            </div>
          </div>

          <Separator orientation="vertical" />

          {/* Right: Preview Results */}
          <div className="w-1/2 overflow-hidden flex flex-col">
            <h4 className="text-sm font-medium mb-3">Preview Results</h4>

            {!previewResults ? (
              <div className="flex-1 flex items-center justify-center text-center text-muted-foreground">
                <div>
                  <Settings2 className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">
                    Adjust toggles and click "Preview Changes" to see results
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Stats Summary */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Card className="p-3 text-center">
                    <div className="text-lg font-bold">
                      {previewResults.stats.vendorsExtracted}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Vendors Extracted
                    </div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-lg font-bold">
                      {previewResults.stats.prefixesStripped}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Prefixes Stripped
                    </div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-lg font-bold">
                      {previewResults.stats.aliasesMerged}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Aliases Merged
                    </div>
                  </Card>
                  <Card className="p-3 text-center">
                    <div className="text-lg font-bold">
                      {previewResults.stats.totalProcessed}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Total Processed
                    </div>
                  </Card>
                </div>

                {/* Vendor Preview */}
                <ScrollArea className="flex-1">
                  <div className="space-y-2">
                    {previewResults.vendors.slice(0, 20).map((vendor, i) => (
                      <Card key={i} className="p-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="flex-1 truncate text-muted-foreground font-mono">
                            {vendor.original}
                          </span>
                          <ArrowRight className="h-3 w-3 flex-shrink-0" />
                          <span className="flex-1 truncate font-medium">
                            {vendor.normalized}
                          </span>
                          <Badge
                            variant="outline"
                            className="text-xs flex-shrink-0"
                          >
                            {vendor.confidence}%
                          </Badge>
                        </div>
                        {vendor.appliedRules.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {vendor.appliedRules.map((rule, j) => (
                              <Badge
                                key={j}
                                variant="secondary"
                                className="text-xs"
                              >
                                {rule.replace(/_/g, " ")}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </Card>
                    ))}
                    {previewResults.vendors.length > 20 && (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        +{previewResults.vendors.length - 20} more vendors
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!previewResults}>
            <Check className="h-4 w-4 mr-2" />
            Apply Tuning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
